import LBox from "@components/LBox";
import LButton from "@components/LButton";
import LText from "@components/LText";
import React from "react";
import { FadeInUp, Easing } from "react-native-reanimated";
import type { Stats } from "@libs/types/stats";
import { currencyFormat } from "@utils/index";
import SkeletonPlaceholder from "react-native-skeleton-placeholder";

type StatsIncomeProps = {
  revenue: Stats["Revenue"]["Output"]["data"] | undefined | null;
};

const StatsSkeleton = () => (
  <LBox flex={1} alignItems="center">
    <SkeletonPlaceholder
      highlightColor="rgba(255,255,255,1)"
      backgroundColor="rgba(255,255,255,0.3)"
      speed={1200}
    >
      <SkeletonPlaceholder.Item alignItems="center" gap={30}>
        <SkeletonPlaceholder.Item width={120} height={20} borderRadius={5} />
        <SkeletonPlaceholder.Item width={270} height={60} borderRadius={5} />
      </SkeletonPlaceholder.Item>
    </SkeletonPlaceholder>
  </LBox>
);

const StatsIncome = ({ revenue }: StatsIncomeProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  if (!revenue) return <StatsSkeleton />;

  // Defensive: a stale/partial cached payload could lack `songs`; never let the
  // breakdown crash the app when expanded.
  const songs = revenue.songs ?? [];

  return (
    <LBox alignItems={"center"}>
      <LText variant="h3" textAlign="center" color="inboxDate">
        Your songs daily
      </LText>
      <LBox my="m1" flexDirection="row" alignItems="center" alignSelf="center">
        <LBox>
          <LText variant="lead" textAlign="center" color="homeGreeting" mb="s2">
            {currencyFormat(revenue?.your_share ?? 0)}
          </LText>
          <LText variant="bodyS" numberOfLines={2} textAlign="center">
            {"Your share"}
          </LText>
        </LBox>
        <LText variant="lead" textAlign="center" mx="l1">
          /
        </LText>
        <LBox>
          <LText variant="lead" textAlign="center" color="homeGreeting" mb="s2">
            {currencyFormat(revenue?.total_revenue ?? 0)}
          </LText>
          <LText variant="bodyS" numberOfLines={2} textAlign="center">
            Total income
          </LText>
        </LBox>
      </LBox>
      <LBox width="100%" alignSelf="center">
        {songs.length ? (
          <LButton
            sentryEventName="stats_income_show_hide_breakdown"
            variant="tertiary-small"
            label={isOpen ? "Hide breakdown" : "Show breakdown"}
            onPress={() => setIsOpen((prev) => !prev)}
            textVariant="bodyM"
          />
        ) : null}
      </LBox>
      {isOpen ? (
        <LBox mt="s4">
          <LText
            variant="bodyS"
            color="homeGreeting"
            textAlign="center"
            mb="s1"
          >
            Values under £0.01 are not displayed
          </LText>
          <LBox flexDirection="row" mb="s2">
            <LBox width="40%"></LBox>
            <LBox width="15%" alignItems="center" justifyContent="center">
              <LText variant="bodyS" numberOfLines={2} textAlign="center">
                Streams
              </LText>
            </LBox>
            <LBox width="15%" alignItems="center">
              <LText variant="bodyS" numberOfLines={2} textAlign="center">
                {"Stream time"}
              </LText>
            </LBox>
            <LBox width="15%" alignItems="center">
              <LText variant="bodyS" numberOfLines={2} textAlign="center">
                {"Your share"}
              </LText>
            </LBox>
            <LBox width="15%" alignItems="center">
              <LText variant="bodyS" numberOfLines={2} textAlign="center">
                {"Total income"}
              </LText>
            </LBox>
          </LBox>
          {songs.map((song, index) => (
            <LBox
              key={index}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              entering={FadeInUp.duration(125)
                .easing(Easing.inOut(Easing.ease))
                .delay((index ?? 0) * 50)}
              mb={index === songs.length - 1 ? "zero" : "s2"}
            >
              <LBox width="40%">
                <LText variant="bodyM">{song.title}</LText>
                <LText variant="bodyS" color="inboxDate">
                  by {song.artist}
                </LText>
              </LBox>
              <LBox width="15%" alignItems="center">
                <LText variant="bodyM">{song.streams}</LText>
              </LBox>
              <LBox width="15%" alignItems="center">
                <LText variant="bodyM">
                  {(song.stream_time / 60).toFixed(1)}m
                </LText>
              </LBox>
              <LBox width="15%" alignItems="center">
                <LText variant="bodyM">{currencyFormat(song.your_share)}</LText>
              </LBox>
              <LBox width="15%" alignItems="center">
                <LText variant="bodyM">
                  {currencyFormat(song.total_royalties)}
                </LText>
              </LBox>
            </LBox>
          ))}
        </LBox>
      ) : null}
    </LBox>
  );
};

export default StatsIncome;
